import { CommonModule } from '@angular/common'
import { HttpClient } from '@angular/common/http'
import {
  ChangeDetectorRef,
  Component,
  DestroyRef,
  ElementRef,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { FormsModule } from '@angular/forms'
import { MatButtonModule } from '@angular/material/button'
import { MatChipsModule } from '@angular/material/chips'
import { MatDialog } from '@angular/material/dialog'
import { MatIconModule } from '@angular/material/icon'
import { MatMenuModule } from '@angular/material/menu'
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner'
import { MatTooltipModule } from '@angular/material/tooltip'
import { ActivatedRoute, Router } from '@angular/router'
import { HocuspocusProvider } from '@hocuspocus/provider'
import { injectQueryClient } from '@ngneat/query'
import { TranslateModule, TranslateService } from '@ngx-translate/core'
import { AnyExtension, ChainedCommands, Editor } from '@tiptap/core'
import CharacterCount from '@tiptap/extension-character-count'
import Collaboration from '@tiptap/extension-collaboration'
import CollaborationCaret from '@tiptap/extension-collaboration-caret'
import { Mark as ProseMirrorMark } from '@tiptap/pm/model'
import { EditorState, PluginKey, TextSelection, Transaction } from '@tiptap/pm/state'
import { EditorView } from '@tiptap/pm/view'
import { TiptapBubbleMenuDirective, TiptapEditorDirective } from 'ngx-tiptap'
import { BehaviorSubject, filter, lastValueFrom, map, Subscription, switchMap, take } from 'rxjs'

import * as Y from 'yjs'
import { swapListType } from '../../../../../backend/src/tiptap/editor/extensions/nodes/list'
import {
  FormattingChanges,
  LIST_ITEM_NODE_NAME,
  ListChange,
  SUGGESTION_MARK_NAME,
  SUGGESTION_TYPE_DELETION,
  SUGGESTION_TYPE_FORMATTING,
  SUGGESTION_TYPE_INSERTION,
  SuggestionType,
} from '../../../../../backend/src/tiptap/editor/extensions/marks/suggestion'
import { EDITOR_STRUCTURAL_EXTENSIONS } from '../../../../../backend/src/tiptap/editor-extensions'
import { Call, MetricType } from '../../calls.service'
import { LoggerService } from '../../logger.service'
import { MetricsService } from '../../metrics.service'
import { queryKeys } from '../../query-keys'
import { ToastService } from '../../toast.service'
import { UsersService } from '../../users.service'
import { constructNameForUser, stringArraysEqual } from '../../utils'
import { AiActionFactCheckDialogComponent, AiActionFactCheckDialogData } from './ai-actions/ai-action-fact-check-dialog.component'
import { AiActionStreamingDialogComponent, AiActionStreamingDialogData } from './ai-actions/ai-action-streaming-dialog.component'
import { AI_ACTION_CONFIGS, LlmAction, SentenceIdPair } from './ai-actions/ai-action.types'
import {
  expandSelectionToWordBoundaries,
  getSelectedText,
  getSentenceIdPairsFromSelection,
  getTemplateItemIdsFromSelection,
} from './ai-actions/selection-utils'
import { EDITOR_MAXIMIZED_QUERY_PARAM, EditorSettingsService } from '../editor/editor-settings.service'
import { AgendaMissingWarningComponent } from '../agenda-missing-warning/agenda-missing-warning.component'
import { CitationExtension } from './extensions/citation-extension'
import { citationPluginKey } from './extensions/citation-plugin'
import { liftListItemSingleLevel, listChangeArraysEqual, restoreListItemAtSource, runListCommandWithTracking } from './extensions/list-track-changes'
import { TrackChangesExtension } from './extensions/track-changes-extension'
import { TRACK_CHANGES_SKIP_META, trackChangesPluginKey } from './extensions/track-changes-plugin'
import { SseSyncProvider } from './sse-sync-provider'
import { SuggestionPopupComponent, SuggestionTextRun } from './suggestion-popup/suggestion-popup.component'
import { hslToHex } from './utils/hsl-to-hex'

type ActiveSuggestion = {
  suggestionId: string
  authorName: string
  insertionRuns: SuggestionTextRun[]
  deletionRuns: SuggestionTextRun[]
  formattingAdded: string[]
  formattingRemoved: string[]
  listChanges: ListChange[]
}

const NON_DISPLAY_MARK_NAMES = new Set([SUGGESTION_MARK_NAME, 'chunk'])

function collectDisplayMarkNames(marks: readonly { type: { name: string } }[]): string[] {
  return marks.filter((mark) => !NON_DISPLAY_MARK_NAMES.has(mark.type.name)).map((mark) => mark.type.name)
}

function runsEqual(left: SuggestionTextRun[], right: SuggestionTextRun[]): boolean {
  if (left.length !== right.length) {
    return false
  }
  for (let index = 0; index < left.length; index++) {
    if (left[index].text !== right[index].text) {
      return false
    }
    if (!stringArraysEqual(left[index].marks, right[index].marks)) {
      return false
    }
  }
  return true
}

const STATELESS_REFETCH_CALL_MESSAGE_TYPE = 'refetch-call'
const STATELESS_TRACK_CHANGES_PREFIX = 'track-changes-update:'

const SUGGESTION_POPUP_EDGE_PADDING = 8
// Mirrors the `max-height` cap in suggestion-popup.component.scss so the inline
// style only ever tightens the height — never overrides the SCSS cap on tall editors.
const SUGGESTION_POPUP_MAX_HEIGHT_CAP = 'min(30vh, 210px)'

type SuggestionPopupOptions = {
  strategy: 'fixed' | 'absolute'
  scrollTarget?: HTMLElement
  flip?: boolean
  shift?: { padding?: number }
  size?: { padding?: number; apply?: (args: { availableHeight: number; elements: { floating: HTMLElement } }) => void }
}

function buildSuggestionPopupOptions(scrollTarget?: HTMLElement): SuggestionPopupOptions {
  return {
    strategy: 'absolute',
    scrollTarget,
    flip: true,
    shift: { padding: SUGGESTION_POPUP_EDGE_PADDING },
    size: {
      padding: SUGGESTION_POPUP_EDGE_PADDING,
      apply: ({ availableHeight, elements }) => {
        const next = `min(${Math.max(0, availableHeight - SUGGESTION_POPUP_EDGE_PADDING)}px, ${SUGGESTION_POPUP_MAX_HEIGHT_CAP})`
        // `apply` runs on every transaction and on every (debounced) scroll. Skipping
        // identical writes avoids a per-keystroke style invalidation on the popover.
        if (elements.floating.style.maxHeight !== next) {
          elements.floating.style.maxHeight = next
        }
      },
    },
  }
}

function findScrollableAncestor(element: HTMLElement): HTMLElement | null {
  let current: HTMLElement | null = element
  while (current !== null) {
    const overflowY = window.getComputedStyle(current).overflowY
    if ((overflowY === 'auto' || overflowY === 'scroll') && current.scrollHeight > current.clientHeight) {
      return current
    }
    current = current.parentElement
  }
  return null
}

@Component({
  selector: 'app-tiptap-editor',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatButtonModule,
    MatChipsModule,
    MatIconModule,
    MatMenuModule,
    MatProgressSpinnerModule,
    MatTooltipModule,
    TranslateModule,
    TiptapEditorDirective,
    TiptapBubbleMenuDirective,
    AgendaMissingWarningComponent,
    SuggestionPopupComponent,
  ],
  templateUrl: './tiptap-editor.component.html',
  styleUrl: './tiptap-editor.component.scss',
})
export class TiptapEditorComponent implements OnInit, OnChanges, OnDestroy {
  @Input({ required: true })
  call!: Call

  @Input({ required: true })
  canUserEdit!: boolean

  @Output()
  openDocument = new EventEmitter<{ resourceId: number; resourcePageNumber: number }>()

  @Output()
  openMeeting = new EventEmitter<void>()

  @Output()
  openAgendaUpload = new EventEmitter<void>()

  usersService = inject(UsersService)
  private http = inject(HttpClient)
  private dialog = inject(MatDialog)
  private toastService = inject(ToastService)
  private metricsService = inject(MetricsService)
  private translateService = inject(TranslateService)
  private editorSettingsService = inject(EditorSettingsService)
  private logger = inject(LoggerService)
  private changeDetectorRef = inject(ChangeDetectorRef)
  private route = inject(ActivatedRoute)
  private router = inject(Router)
  private queryClient = injectQueryClient()
  private destroyRef = inject(DestroyRef)

  document: Y.Doc | null = null

  hocuspocus: HocuspocusProvider | null = null

  editor: Editor | null = null
  hasInitiallySynced = false
  showWordCount$ = this.editorSettingsService.showWordCount$
  isEditorMaximized$ = this.editorSettingsService.isEditorMaximized$

  // The bubble menu is only rendered after the document has finished syncing, so this
  // ViewChild resolves later than `ngAfterViewInit` would fire. Using a setter lets us
  // attach the scroll-driven hide handler the first time Angular gives us a real ref.
  @ViewChild(TiptapBubbleMenuDirective, { read: ElementRef })
  set bubbleMenuElementRef(ref: ElementRef<HTMLElement> | undefined) {
    const element = ref?.nativeElement ?? null
    if (element === null || this.editor === null || this.bubbleMenuScrollHandler !== null) {
      return
    }
    this.attachBubbleMenuScrollHandler(this.editor, element)
  }

  aiActionsConfig = AI_ACTION_CONFIGS
  bubbleMenuOptions: { strategy: 'fixed' | 'absolute'; scrollTarget?: HTMLElement } = { strategy: 'absolute' }
  suggestionPopupOptions: SuggestionPopupOptions = buildSuggestionPopupOptions()

  trackChangesEnabled$ = new BehaviorSubject<boolean>(false)
  trackChangesPending$ = new BehaviorSubject<boolean>(false)
  // Pre-translated label for the toolbar toggle so the template binding is a primitive
  // string read instead of running the impure `translate` pipe over a ternary on every
  // change-detection cycle.
  trackChangesToggleLabel$ = this.trackChangesEnabled$.pipe(
    switchMap((enabled) => this.translateService.stream(enabled ? 'EDITOR.TRACK_CHANGES.DISABLE_TOOLTIP' : 'EDITOR.TRACK_CHANGES.ENABLE_TOOLTIP'))
  )
  activeSuggestion$ = new BehaviorSubject<ActiveSuggestion | null>(null)
  suggestionCount$ = new BehaviorSubject<number>(0)

  readonly suggestionPopupPluginKey = new PluginKey('suggestionPopup')

  private citationsSubscription: Subscription | null = null
  private templateItemSubscription: Subscription | null = null
  private synced$ = new BehaviorSubject<boolean>(false)
  private bubbleMenuScrollHandler: (() => void) | null = null
  private bubbleMenuScrollDebounceTimer: number | null = null
  private suggestionRepositionRafId: number | null = null

  shouldShowSuggestionPopup = (props: { state: EditorState; oldState?: EditorState; from: number; to: number }): boolean => {
    if (this.editor === null) {
      this.refreshActiveSuggestion(null)
      return false
    }

    if (props.from !== props.to) {
      this.refreshActiveSuggestion(null)
      return false
    }

    // Suppress the popover while the document is changing — e.g. the user is
    // typing a tracked change. The popover should only appear when the cursor
    // is manually navigated onto an existing suggestion.
    const oldState = props.oldState ?? null
    if (oldState !== null && !oldState.doc.eq(props.state.doc)) {
      this.refreshActiveSuggestion(null)
      return false
    }

    const suggestion = this.findSuggestionAtPosition(props.state, props.from)
    if (suggestion === null) {
      this.refreshActiveSuggestion(null)
      return false
    }

    const fallbackAuthor = this.translateService.instant('EDITOR.TRACK_CHANGES.UNKNOWN_AUTHOR')
    const collected = this.collectSuggestionContent(props.state, suggestion.suggestionId)

    this.refreshActiveSuggestion({
      suggestionId: suggestion.suggestionId,
      authorName: suggestion.authorName ?? fallbackAuthor,
      insertionRuns: collected.insertionRuns,
      deletionRuns: collected.deletionRuns,
      formattingAdded: collected.formattingAdded,
      formattingRemoved: collected.formattingRemoved,
      listChanges: collected.listChanges,
    })
    return true
  }

  ngOnInit() {
    const user = this.usersService.currentUser$.value
    const name = user !== null ? constructNameForUser(user) : 'Unknown User'

    this.document = new Y.Doc()

    this.hocuspocus = new SseSyncProvider({
      name: `minutes:${this.call.id}`,
      document: this.document,
      onSynced: ({ state }) => {
        if (!this.hasInitiallySynced && state) {
          this.hasInitiallySynced = true
          this.synced$.next(true)
          this.changeDetectorRef.detectChanges()
        }
      },
      onStatelessPayload: (payload) => {
        if (payload === STATELESS_REFETCH_CALL_MESSAGE_TYPE) {
          this.queryClient.invalidateQueries({ queryKey: queryKeys.calls.byId(this.call.id) })
        } else if (payload.startsWith(STATELESS_TRACK_CHANGES_PREFIX)) {
          const enabled = payload === `${STATELESS_TRACK_CHANGES_PREFIX}true`
          this.trackChangesEnabled$.next(enabled)
        }
      },
    })

    const extensions: AnyExtension[] = [
      ...EDITOR_STRUCTURAL_EXTENSIONS,
      CharacterCount,
      Collaboration.configure({
        document: this.document,
      }),
      CollaborationCaret.configure({
        provider: this.hocuspocus,
        user: {
          name,
          color: hslToHex(Math.floor(Math.random() * 360), 0.75, 0.8),
        },
      }),
      TrackChangesExtension.configure({
        getEnabled: () => this.trackChangesEnabled$.value,
        getCurrentUser: () => {
          const currentUser = this.usersService.currentUser$.value
          if (currentUser === null) {
            return null
          }
          return { id: currentUser.id, name: constructNameForUser(currentUser) }
        },
      }),
    ]

    if (this.call.minutesCitations !== null) {
      extensions.push(
        CitationExtension.configure({
          getCitations: () => this.call.minutesCitations!,
          getResources: () => this.call.userUploadedResources.map((resource) => ({ id: resource.id, fileName: resource.fileName })),
          formatPageLabel: (page: number) => this.translateService.instant('EDITOR.CITATIONS.PAGE_NUMBER', { pageNumber: page }),
          onOpenDocument: (event) => this.openDocument.emit(event),
          onOpenMeeting: (event) => this.handleOpenMeeting(event),
        })
      )
    }

    this.editor = new Editor({
      extensions,
      editorProps: {
        handleScrollToSelection: (view) => this.smoothScrollToSelection(view),
      },
    })
    this.editor.setEditable(this.canUserEdit)
    this.bubbleMenuOptions = { strategy: 'absolute', scrollTarget: this.editor.view.dom as HTMLElement }
    this.suggestionPopupOptions = buildSuggestionPopupOptions(this.editor.view.dom as HTMLElement)

    this.editor.on('update', () => this.refreshSuggestionCount())
    this.trackChangesEnabled$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((enabled) => {
      if (enabled) {
        this.refreshSuggestionCount()
      }
    })

    // The bubble menu computes its position before Angular renders the popover's
    // content, so the element measures at zero width and `shift` sees no overflow.
    // Re-trigger an update once the suggestion content has been laid out.
    this.activeSuggestion$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((suggestion) => {
      if (suggestion === null || this.editor === null) {
        return
      }
      if (this.suggestionRepositionRafId !== null) {
        cancelAnimationFrame(this.suggestionRepositionRafId)
      }
      this.suggestionRepositionRafId = requestAnimationFrame(() => {
        this.suggestionRepositionRafId = null
        if (this.editor === null) {
          return
        }
        this.editor.view.dispatch(this.editor.state.tr.setMeta(this.suggestionPopupPluginKey, 'updatePosition'))
      })
    })

    this.http
      .get<{ enabled: boolean }>(`/api/tiptap-editor/document/${this.call.id}/track-changes`)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: ({ enabled }) => this.trackChangesEnabled$.next(enabled),
        error: (error) => this.logger.error('Failed to fetch track changes state', error),
      })

    if (this.call.minutesCitations !== null) {
      this.citationsSubscription = this.editorSettingsService.showCitations$.subscribe((showCitations) => {
        const dom = this.editor?.view.dom ?? null

        if (dom === null) {
          return
        }

        if (showCitations) {
          dom.classList.remove('citations-hidden')
        } else {
          dom.classList.add('citations-hidden')
        }
      })
    }

    this.templateItemSubscription = this.route.queryParams
      .pipe(
        map((params) => params['templateItem'] ?? null),
        filter((templateItemId) => templateItemId !== null),
        switchMap((templateItemId) =>
          this.synced$.pipe(
            filter((synced) => synced),
            take(1),
            map(() => templateItemId)
          )
        )
      )
      .subscribe((templateItemId) => {
        this.scrollToTemplateItem(Number(templateItemId))
        this.router.navigate([], {
          queryParams: { templateItem: null },
          queryParamsHandling: 'merge',
          replaceUrl: true,
        })
      })
  }

  ngOnChanges(changes: SimpleChanges) {
    if (this.editor === null || changes['call'] === undefined || changes['call'].firstChange) {
      return
    }

    const previousCall = changes['call'].previousValue as Call
    if (previousCall.minutesCitations !== this.call.minutesCitations) {
      this.editor.view.dispatch(this.editor.state.tr.setMeta(citationPluginKey, { type: 'REBUILD' }))
    }
  }

  private attachBubbleMenuScrollHandler(editor: Editor, bubbleMenuElement: HTMLElement) {
    this.bubbleMenuScrollHandler = () => {
      if (!editor.state.selection.empty) {
        return
      }

      if (this.bubbleMenuScrollDebounceTimer !== null) {
        clearTimeout(this.bubbleMenuScrollDebounceTimer)
      }

      // Must exceed the bubble menu plugin's internal resizeDelay (60ms) so our
      // visibility correction runs after the plugin's scroll-triggered updatePosition().
      this.bubbleMenuScrollDebounceTimer = window.setTimeout(() => {
        if (editor.state.selection.empty) {
          bubbleMenuElement.style.visibility = 'hidden'
        }
      }, 70)
    }

    editor.view.dom.addEventListener('scroll', this.bubbleMenuScrollHandler, { passive: true })
  }

  ngOnDestroy() {
    if (this.bubbleMenuScrollDebounceTimer !== null) {
      clearTimeout(this.bubbleMenuScrollDebounceTimer)
    }
    if (this.suggestionRepositionRafId !== null) {
      cancelAnimationFrame(this.suggestionRepositionRafId)
      this.suggestionRepositionRafId = null
    }
    if (this.bubbleMenuScrollHandler !== null && this.editor !== null) {
      this.editor.view.dom.removeEventListener('scroll', this.bubbleMenuScrollHandler)
    }
    this.citationsSubscription?.unsubscribe()
    this.templateItemSubscription?.unsubscribe()
    this.synced$.complete()
    this.trackChangesEnabled$.complete()
    this.trackChangesPending$.complete()
    this.activeSuggestion$.complete()
    this.suggestionCount$.complete()
    this.editor?.destroy()
    this.hocuspocus?.destroy()
    this.document?.destroy()
  }

  toggleTrackChanges() {
    if (this.trackChangesPending$.value) {
      return
    }

    const previousValue = this.trackChangesEnabled$.value
    const nextValue = !previousValue
    this.trackChangesEnabled$.next(nextValue)
    this.trackChangesPending$.next(true)

    this.http
      .put<{ enabled: boolean }>(`/api/tiptap-editor/document/${this.call.id}/track-changes`, { enabled: nextValue })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => this.trackChangesPending$.next(false),
        error: (error) => {
          this.trackChangesEnabled$.next(previousValue)
          this.trackChangesPending$.next(false)
          this.toastService.show('EDITOR.TRACK_CHANGES.UPDATE_FAILED')
          this.logger.error('Failed to update track changes state', error)
        },
      })
  }

  acceptSuggestion(suggestionId: string) {
    this.resolveSuggestion(suggestionId, 'accept')
  }

  rejectSuggestion(suggestionId: string) {
    this.resolveSuggestion(suggestionId, 'reject')
  }

  private resolveSuggestion(suggestionId: string, action: 'accept' | 'reject') {
    if (this.editor === null) {
      return
    }

    const suggestionMarkType = this.editor.state.schema.marks[SUGGESTION_MARK_NAME] ?? null
    if (suggestionMarkType === null) {
      return
    }

    if (action === 'reject') {
      this.invertListChangeSuggestions(suggestionId)
    }

    const ranges = this.findAllRangesForSuggestion(this.editor.state, suggestionId)
    if (ranges.length === 0) {
      return
    }

    const transaction = this.editor.state.tr
    const sortedRanges = [...ranges].sort((rangeA, rangeB) => rangeB.from - rangeA.from)
    const schema = this.editor.state.schema

    for (const range of sortedRanges) {
      // A single suggestionId may cover insertion, deletion, and formatting ranges;
      // each range is resolved against its own type, not the suggestion's first range.
      if (range.type === SUGGESTION_TYPE_FORMATTING) {
        if (action === 'reject' && range.formattingChanges !== null) {
          // Undo the tracked formatting change before dropping the suggestion mark.
          for (const markName of range.formattingChanges.added) {
            const markType = schema.marks[markName] ?? null
            if (markType !== null) {
              transaction.removeMark(range.from, range.to, markType)
            }
          }
          for (const markName of range.formattingChanges.removed) {
            const markType = schema.marks[markName] ?? null
            if (markType !== null) {
              transaction.addMark(range.from, range.to, markType.create())
            }
          }
        }
        transaction.removeMark(range.from, range.to, suggestionMarkType)
        continue
      }

      const removeMark = (action === 'accept' && range.type === SUGGESTION_TYPE_INSERTION) || (action === 'reject' && range.type === SUGGESTION_TYPE_DELETION)
      if (removeMark) {
        transaction.removeMark(range.from, range.to, suggestionMarkType)
      } else {
        transaction.delete(range.from, range.to)
      }
    }

    transaction.setMeta(trackChangesPluginKey, TRACK_CHANGES_SKIP_META)
    this.editor.view.dispatch(transaction)
    this.refreshActiveSuggestion(null)
  }

  // Inverts every listChange entry on the suggestion's formatting ranges, applied
  // in reverse order so the most recent change is rolled back first. Each inversion
  // dispatches its own transaction with TRACK_CHANGES_SKIP_META; positions are
  // re-resolved before each step in case prior inversions shifted the range.
  // The iteration cap is a safety net — each step is supposed to drop one entry,
  // so we never need more iterations than the largest possible listChanges length
  // we could see. A standalone count keeps an unexpected no-op step from looping.
  private static readonly LIST_INVERSION_ITERATION_CAP = 64

  private invertListChangeSuggestions(suggestionId: string) {
    if (this.editor === null) {
      return
    }

    for (let iteration = 0; iteration < TiptapEditorComponent.LIST_INVERSION_ITERATION_CAP; iteration++) {
      const target = this.findNextListChangeInversionTarget(suggestionId)
      if (target === null) {
        return
      }
      this.dispatchStructuralInversion(suggestionId, target.rangeFrom, target.listChange)
      this.dispatchListChangesRewrite(suggestionId, target.remainingListChanges)
    }
  }

  // Returns the union range of all formatting suggestion ranges (with non-empty
  // listChanges) for the given suggestionId, plus the most-recent listChange entry
  // and the remaining ones.
  private findNextListChangeInversionTarget(
    suggestionId: string
  ): { rangeFrom: number; rangeTo: number; listChange: ListChange; remainingListChanges: ListChange[] } | null {
    if (this.editor === null) {
      return null
    }
    const ranges = this.findAllRangesForSuggestion(this.editor.state, suggestionId)
    let rangeFrom = Number.POSITIVE_INFINITY
    let rangeTo = Number.NEGATIVE_INFINITY
    let listChanges: ListChange[] | null = null
    for (const range of ranges) {
      if (range.type !== SUGGESTION_TYPE_FORMATTING) {
        continue
      }
      const candidates = range.formattingChanges?.listChanges ?? []
      if (candidates.length === 0) {
        continue
      }
      // Every formatting range under one suggestionId carries the same listChanges
      // array, so we read it once and span our selection across all ranges so
      // multi-item lists get inverted in one go.
      listChanges = listChanges ?? candidates
      if (range.from < rangeFrom) {
        rangeFrom = range.from
      }
      if (range.to > rangeTo) {
        rangeTo = range.to
      }
    }
    if (listChanges === null) {
      return null
    }
    const lastIndex = listChanges.length - 1
    return {
      rangeFrom,
      rangeTo,
      listChange: listChanges[lastIndex],
      remainingListChanges: listChanges.slice(0, lastIndex),
    }
  }

  private dispatchStructuralInversion(suggestionId: string, rangeFrom: number, listChange: ListChange) {
    if (listChange.kind === 'wrap') {
      // `wrap` introduced both a list and a listItem; lift twice in one chain.
      this.dispatchInversionChain(rangeFrom, (chain) => chain.command(liftListItemSingleLevel).command(liftListItemSingleLevel))
      return
    }
    if (listChange.kind === 'unwrap') {
      this.dispatchInversionChain(rangeFrom, (chain) => chain.wrapInList(listChange.listType))
      return
    }
    if (listChange.kind === 'changeListType') {
      this.dispatchInversionChain(rangeFrom, (chain) =>
        chain.command(({ state, tr, dispatch }) => {
          const targetType = state.schema.nodes[listChange.from] ?? null
          if (targetType === null) {
            return false
          }
          return swapListType(state, dispatch, tr, listChange.to, targetType)
        })
      )
      return
    }
    if (listChange.source !== undefined) {
      const source = listChange.source
      this.dispatchInversionChain(rangeFrom, (chain) => chain.command(restoreListItemAtSource(source)))
      return
    }
    // Older suggestions captured before sourcePosition was recorded fall back to
    // per-level steps. Mirror the original direction: positive delta (indent
    // applied via sink) inverts with lift; negative delta (outdent applied via
    // lift) inverts with sink. Each step may move the item, so re-resolve the range.
    const count = Math.abs(listChange.delta)
    const invertStep: (chain: ChainedCommands) => ChainedCommands =
      listChange.delta > 0 ? (chain) => chain.command(liftListItemSingleLevel) : (chain) => chain.sinkListItem(LIST_ITEM_NODE_NAME)
    for (let index = 0; index < count; index++) {
      const range = this.firstFormattingRangeForSuggestion(suggestionId)
      if (range === null) {
        return
      }
      const dispatched = this.dispatchInversionChain(range.from, invertStep)
      if (!dispatched) {
        return
      }
    }
  }

  private dispatchInversionChain(rangeFrom: number, applyChain: (chain: ChainedCommands) => ChainedCommands): boolean {
    if (this.editor === null) {
      return false
    }
    // Place a cursor at the start of the range. PM list commands consult the selection
    // to find the listItem ancestor; a cursor produces a single, well-defined block
    // range, while a wide selection can confuse `liftListItem` into lifting too far.
    const chain = this.editor.chain().setTextSelection(rangeFrom)
    return applyChain(chain)
      .command(({ tr }) => {
        tr.setMeta(trackChangesPluginKey, TRACK_CHANGES_SKIP_META)
        return true
      })
      .run()
  }

  private firstFormattingRangeForSuggestion(suggestionId: string): { from: number; to: number } | null {
    if (this.editor === null) {
      return null
    }
    let from = Number.POSITIVE_INFINITY
    let to = Number.NEGATIVE_INFINITY
    let found = false
    for (const range of this.findAllRangesForSuggestion(this.editor.state, suggestionId)) {
      if (range.type !== SUGGESTION_TYPE_FORMATTING) {
        continue
      }
      found = true
      if (range.from < from) {
        from = range.from
      }
      if (range.to > to) {
        to = range.to
      }
    }
    return found ? { from, to } : null
  }

  private dispatchListChangesRewrite(suggestionId: string, remaining: ListChange[]) {
    if (this.editor === null) {
      return
    }
    const transaction = this.editor.state.tr
    this.rewriteSuggestionListChanges(transaction, suggestionId, remaining)
    if (transaction.steps.length === 0) {
      return
    }
    transaction.setMeta(trackChangesPluginKey, TRACK_CHANGES_SKIP_META)
    this.editor.view.dispatch(transaction)
  }

  // Replace the suggestion's formattingChanges.listChanges with `remaining`. When
  // `remaining` is empty AND added/removed are empty, drop the mark entirely so the
  // outer mark-removal pass has nothing to do here.
  private rewriteSuggestionListChanges(tr: Transaction, suggestionId: string, remaining: ListChange[]) {
    if (this.editor === null) {
      return
    }
    const suggestionMarkType = this.editor.state.schema.marks[SUGGESTION_MARK_NAME] ?? null
    if (suggestionMarkType === null) {
      return
    }

    // Touch every text node carrying the mark — sibling nodes that differ in
    // their other inline marks each hold their own suggestion-mark instance,
    // and skipping any of them would leave stale listChanges behind for the
    // next iteration of the inversion loop to find.
    const updates: { from: number; to: number; oldMark: ProseMirrorMark; nextAttrs: Record<string, unknown> | null }[] = []

    tr.doc.descendants((node, pos) => {
      if (!node.isText) {
        return true
      }
      const mark =
        node.marks.find(
          (candidate) =>
            candidate.type.name === SUGGESTION_MARK_NAME &&
            candidate.attrs['suggestionId'] === suggestionId &&
            candidate.attrs['type'] === SUGGESTION_TYPE_FORMATTING
        ) ?? null
      if (mark === null) {
        return false
      }

      const formattingChanges = (mark.attrs['formattingChanges'] as FormattingChanges | null) ?? { added: [], removed: [] }
      const nextFormatting: FormattingChanges = { added: formattingChanges.added, removed: formattingChanges.removed }
      if (remaining.length > 0) {
        nextFormatting.listChanges = remaining
      }
      const isEmpty = nextFormatting.added.length === 0 && nextFormatting.removed.length === 0 && (nextFormatting.listChanges ?? []).length === 0
      const nextAttrs = isEmpty ? null : { ...mark.attrs, formattingChanges: nextFormatting }
      updates.push({ from: pos, to: pos + node.nodeSize, oldMark: mark, nextAttrs })
      return false
    })

    for (const update of updates) {
      tr.removeMark(update.from, update.to, update.oldMark)
      if (update.nextAttrs !== null) {
        tr.addMark(update.from, update.to, suggestionMarkType.create(update.nextAttrs))
      }
    }
  }

  gotoNextChange() {
    this.gotoAdjacentChange('next')
  }

  gotoPreviousChange() {
    this.gotoAdjacentChange('previous')
  }

  private gotoAdjacentChange(direction: 'next' | 'previous') {
    if (this.editor === null) {
      return
    }

    const startPositions = this.collectSuggestionStartPositions(this.editor.state)
    if (startPositions.length === 0) {
      return
    }

    const currentPosition = this.editor.state.selection.from
    const target =
      direction === 'next'
        ? (startPositions.find((position) => position > currentPosition) ?? startPositions[0])
        : (startPositions.findLast((position) => position < currentPosition) ?? startPositions[startPositions.length - 1])

    const transaction = this.editor.state.tr.setSelection(TextSelection.create(this.editor.state.doc, target))
    transaction.scrollIntoView()
    this.editor.view.dispatch(transaction)
    this.editor.view.focus()
  }

  private collectSuggestionStartPositions(state: EditorState): number[] {
    const seen = new Set<string>()
    const positions: number[] = []

    state.doc.descendants((node, pos) => {
      if (!node.isText) {
        return true
      }
      const suggestionMark = node.marks.find((mark) => mark.type.name === SUGGESTION_MARK_NAME) ?? null
      if (suggestionMark === null) {
        return false
      }
      const suggestionId = suggestionMark.attrs['suggestionId'] as string | null
      if (suggestionId === null || seen.has(suggestionId)) {
        return false
      }
      seen.add(suggestionId)
      positions.push(pos)
      return false
    })

    return positions
  }

  private refreshSuggestionCount() {
    if (this.editor === null || !this.trackChangesEnabled$.value) {
      return
    }
    const count = this.collectSuggestionStartPositions(this.editor.state).length
    if (this.suggestionCount$.value !== count) {
      this.suggestionCount$.next(count)
    }
  }

  private findAllRangesForSuggestion(
    state: EditorState,
    suggestionId: string
  ): { from: number; to: number; type: SuggestionType; formattingChanges: FormattingChanges | null }[] {
    const ranges: { from: number; to: number; type: SuggestionType; formattingChanges: FormattingChanges | null }[] = []

    state.doc.descendants((node, pos) => {
      if (!node.isText) {
        return true
      }

      const suggestionMark = node.marks.find((mark) => mark.type.name === SUGGESTION_MARK_NAME && mark.attrs['suggestionId'] === suggestionId) ?? null
      if (suggestionMark === null) {
        return false
      }

      const type = suggestionMark.attrs['type'] as SuggestionType | null
      if (type !== SUGGESTION_TYPE_INSERTION && type !== SUGGESTION_TYPE_DELETION && type !== SUGGESTION_TYPE_FORMATTING) {
        return false
      }

      const formattingChanges = (suggestionMark.attrs['formattingChanges'] as FormattingChanges | null) ?? null
      ranges.push({ from: pos, to: pos + node.nodeSize, type, formattingChanges })
      return false
    })

    return ranges
  }

  private findSuggestionAtPosition(state: EditorState, position: number): { suggestionId: string; type: SuggestionType; authorName: string | null } | null {
    const resolved = state.doc.resolve(position)

    const fromMarks = resolved.marks().find((mark) => mark.type.name === SUGGESTION_MARK_NAME) ?? null
    const fromBefore = resolved.nodeBefore?.marks.find((mark) => mark.type.name === SUGGESTION_MARK_NAME) ?? null
    const fromAfter = resolved.nodeAfter?.marks.find((mark) => mark.type.name === SUGGESTION_MARK_NAME) ?? null
    const suggestionMark = fromMarks ?? fromBefore ?? fromAfter ?? null

    if (suggestionMark === null) {
      return null
    }

    const suggestionId = suggestionMark.attrs['suggestionId'] as string | null
    const type = suggestionMark.attrs['type'] as SuggestionType | null
    const authorName = suggestionMark.attrs['authorName'] as string | null

    if (suggestionId === null || (type !== SUGGESTION_TYPE_INSERTION && type !== SUGGESTION_TYPE_DELETION && type !== SUGGESTION_TYPE_FORMATTING)) {
      return null
    }

    return { suggestionId, type, authorName }
  }

  private collectSuggestionContent(
    state: EditorState,
    suggestionId: string
  ): {
    insertionRuns: SuggestionTextRun[]
    deletionRuns: SuggestionTextRun[]
    formattingAdded: string[]
    formattingRemoved: string[]
    listChanges: ListChange[]
  } {
    const insertionRuns: SuggestionTextRun[] = []
    const deletionRuns: SuggestionTextRun[] = []
    const addedSet = new Set<string>()
    const removedSet = new Set<string>()
    const listChanges: ListChange[] = []
    const seenFormattingMarks = new Set<ProseMirrorMark>()

    state.doc.descendants((node) => {
      if (!node.isText) {
        return true
      }

      const suggestionMark = node.marks.find((mark) => mark.type.name === SUGGESTION_MARK_NAME && mark.attrs['suggestionId'] === suggestionId) ?? null
      if (suggestionMark === null) {
        return false
      }

      const text = node.text ?? ''
      const type = suggestionMark.attrs['type']
      if (type === SUGGESTION_TYPE_INSERTION) {
        insertionRuns.push({ text, marks: collectDisplayMarkNames(node.marks) })
      } else if (type === SUGGESTION_TYPE_DELETION) {
        deletionRuns.push({ text, marks: collectDisplayMarkNames(node.marks) })
      } else if (type === SUGGESTION_TYPE_FORMATTING) {
        const formattingChanges = suggestionMark.attrs['formattingChanges'] as FormattingChanges | null
        if (formattingChanges !== null) {
          for (const markName of formattingChanges.added) {
            addedSet.add(markName)
          }
          for (const markName of formattingChanges.removed) {
            removedSet.add(markName)
          }
          if (formattingChanges.listChanges !== undefined && !seenFormattingMarks.has(suggestionMark)) {
            seenFormattingMarks.add(suggestionMark)
            for (const listChange of formattingChanges.listChanges) {
              listChanges.push(listChange)
            }
          }
        }
      }
      return false
    })

    return {
      insertionRuns,
      deletionRuns,
      formattingAdded: Array.from(addedSet),
      formattingRemoved: Array.from(removedSet),
      listChanges,
    }
  }

  private refreshActiveSuggestion(next: ActiveSuggestion | null) {
    const current = this.activeSuggestion$.value

    if (current === null && next === null) {
      return
    }
    if (
      current !== null &&
      next !== null &&
      current.suggestionId === next.suggestionId &&
      current.authorName === next.authorName &&
      runsEqual(current.insertionRuns, next.insertionRuns) &&
      runsEqual(current.deletionRuns, next.deletionRuns) &&
      stringArraysEqual(current.formattingAdded, next.formattingAdded) &&
      stringArraysEqual(current.formattingRemoved, next.formattingRemoved) &&
      listChangeArraysEqual(current.listChanges, next.listChanges)
    ) {
      return
    }

    this.activeSuggestion$.next(next)
  }

  toggleBoldOnSelection() {
    this.editor?.chain().focus().toggleBold().run()
  }

  toggleItalicOnSelection() {
    this.editor?.chain().focus().toggleItalic().run()
  }

  toggleBulletList() {
    this.runTrackedListCommand((chain) => chain.toggleBulletList())
  }

  toggleOrderedList() {
    this.runTrackedListCommand((chain) => chain.toggleOrderedList())
  }

  increaseIndent() {
    this.runTrackedListCommand((chain) => chain.sinkListItem(LIST_ITEM_NODE_NAME))
  }

  decreaseIndent() {
    this.runTrackedListCommand((chain) => chain.command(liftListItemSingleLevel))
  }

  private runTrackedListCommand(applyCommand: (chain: ChainedCommands) => ChainedCommands) {
    if (this.editor === null) {
      return
    }
    runListCommandWithTracking(
      this.editor,
      {
        getEnabled: () => this.trackChangesEnabled$.value,
        getCurrentUser: () => {
          const currentUser = this.usersService.currentUser$.value
          if (currentUser === null) {
            return null
          }
          return { id: currentUser.id, name: constructNameForUser(currentUser) }
        },
      },
      applyCommand
    )
  }

  private handleOpenMeeting(event: { startSentenceId: string; endSentenceId: string }) {
    // Clear editor.max in the same navigate as the highlight params; a separate setIsEditorMaximized(false)
    // would race with this call and Angular Router would cancel one of them.
    this.editorSettingsService.updateMaximizedStateOnly(false)
    this.router.navigate([], {
      queryParams: {
        sentenceId: event.startSentenceId,
        endSentenceId: event.endSentenceId,
        highlight: true,
        [EDITOR_MAXIMIZED_QUERY_PARAM]: null,
      },
      queryParamsHandling: 'merge',
    })
    this.openMeeting.emit()
  }

  async reset() {
    if (this.editor === null) {
      return
    }

    try {
      const response = await lastValueFrom(this.http.put<{ content: object }>(`/api/tiptap-editor/document/${this.call.id}/reset`, {}))
      this.editor.commands.setContent(response.content)
    } catch (error) {
      this.toastService.show('EDITOR.RESET_FAILED')
      this.logger.error('Failed to reset editor content', error)
    }
  }

  async performAiAction(action: LlmAction) {
    if (this.editor === null) {
      return
    }

    const actionConfig = AI_ACTION_CONFIGS.find((config) => config.action === action) ?? null
    if (actionConfig === null) {
      return
    }

    try {
      expandSelectionToWordBoundaries(this.editor)
    } catch (error) {
      this.toastService.show('EDITOR.AI_ACTION_FAILED')
      this.logger.error('Failed to expand selection to word boundaries', error)
      return
    }

    const { from, to } = this.editor.state.selection
    const selectedText = getSelectedText(this.editor)

    if (selectedText === null) {
      this.toastService.show('EDITOR.AI_ACTION_FAILED')
      return
    }

    const sentenceIdPairs = getSentenceIdPairsFromSelection(this.editor, this.call.outlineItems)
    const templateItemIds = getTemplateItemIdsFromSelection(this.editor)
    const actionDisplayName = this.translateService.instant(actionConfig.i18nKey)

    this.scrollTranscriptToSentences(sentenceIdPairs)

    try {
      if (actionConfig.stream) {
        await this.handleStreamingAction(action, actionDisplayName, selectedText, sentenceIdPairs, templateItemIds, from, to)
      } else {
        await this.handleSyncAction(action, actionDisplayName, selectedText, sentenceIdPairs, templateItemIds, from, to)
      }
    } catch (error) {
      this.toastService.show('EDITOR.AI_ACTION_FAILED')
      this.logger.error('AI action failed', error)
    }
  }

  private async handleStreamingAction(
    action: LlmAction,
    actionDisplayName: string,
    selectedText: string,
    sentenceIdPairs: SentenceIdPair[],
    templateItemIds: number[],
    selectionFrom: number,
    selectionTo: number
  ) {
    const dialogData: AiActionStreamingDialogData = {
      callId: this.call.id,
      action,
      actionDisplayName,
      originalContent: selectedText,
      sentenceIdPairs,
      templateItemIds,
    }

    const dialogRef = this.dialog.open(AiActionStreamingDialogComponent, {
      data: dialogData,
      hasBackdrop: false,
      autoFocus: false,
      panelClass: 'ai-action-dialog-panel',
    })

    const result = await lastValueFrom(dialogRef.afterClosed())

    if (result !== null && result !== undefined && this.editor !== null) {
      this.metricsService.sendMetric(this.call.id, MetricType.CLICK_LLM_LIVE_ACTION_INSERTION_OF_SUGGESTION)
      this.replaceRangeBypassingTrackChanges(selectionFrom, selectionTo, result.generatedText)
    }
  }

  private async handleSyncAction(
    action: LlmAction,
    actionDisplayName: string,
    selectedText: string,
    sentenceIdPairs: SentenceIdPair[],
    templateItemIds: number[],
    selectionFrom: number,
    selectionTo: number
  ) {
    const dialogData: AiActionFactCheckDialogData = {
      callId: this.call.id,
      action,
      actionDisplayName,
      selectionContent: selectedText,
      sentenceIdPairs,
      templateItemIds,
      onOpenDocument: (event) => this.openDocument.emit(event),
      onOpenMeeting: (event) => this.handleOpenMeeting(event),
    }

    const dialogRef = this.dialog.open(AiActionFactCheckDialogComponent, {
      data: dialogData,
      hasBackdrop: false,
      autoFocus: false,
      panelClass: 'ai-action-dialog-panel',
    })

    const result = await lastValueFrom(dialogRef.afterClosed())

    if (result !== null && result !== undefined && this.editor !== null) {
      this.metricsService.sendMetric(this.call.id, MetricType.CLICK_LLM_LIVE_ACTION_INSERTION_OF_SUGGESTION)
      this.replaceRangeBypassingTrackChanges(selectionFrom, selectionTo, result.insertionText)
    }
  }

  private replaceRangeBypassingTrackChanges(from: number, to: number, content: string) {
    if (this.editor === null) {
      return
    }
    this.editor
      .chain()
      .focus()
      .command(({ tr }) => {
        tr.setMeta(trackChangesPluginKey, TRACK_CHANGES_SKIP_META)
        return true
      })
      .insertContentAt({ from, to }, content)
      .run()
  }

  private smoothScrollToSelection(view: EditorView): boolean {
    const container = findScrollableAncestor(view.dom as HTMLElement)
    if (container === null) {
      return false
    }
    const coords = view.coordsAtPos(view.state.selection.head)
    const rect = container.getBoundingClientRect()
    const margin = 64
    let target = container.scrollTop
    if (coords.bottom > rect.bottom - margin) {
      target += coords.bottom - rect.bottom + margin
    } else if (coords.top < rect.top + margin) {
      target += coords.top - rect.top - margin
    } else {
      return true
    }
    container.scrollTo({ top: Math.max(0, target), behavior: 'smooth' })
    return true
  }

  private scrollToTemplateItem(templateItemId: number) {
    console.debug(`[TipTapEditor] Scrolling to minute node with template item ID ${templateItemId}`)

    if (this.editor === null) {
      return
    }

    const element = this.editor.view.dom.querySelector(`[data-template-item-id="${templateItemId}"]`)

    if (element !== null) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' })
    } else {
      console.warn(`[TipTapEditor] Could not find element to scroll to with selector [data-template-item-id="${templateItemId}"]`)
    }
  }

  private scrollTranscriptToSentences(sentenceIdPairs: SentenceIdPair[]) {
    if (sentenceIdPairs.length === 0) {
      return
    }

    const startSentenceId = sentenceIdPairs[0].startSentenceId
    const endSentenceId = sentenceIdPairs[sentenceIdPairs.length - 1].endSentenceId

    this.router.navigate([], {
      queryParams: { sentenceId: startSentenceId, endSentenceId, highlight: true },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    })
  }
}
